import type { components } from "../../api/generated-schema";
import {
  ApiContractError,
  ApiProblem,
  apiRequest,
  apiRequestWithMeta,
  createRequestId,
} from "../../api/httpClient";
import type { ProblemDetails } from "../../api/httpClient";
import { getAuthenticationSession } from "../../auth/session";

/**
 * API module typed cho hồ sơ liệt sĩ (SCR-025/SCR-026 — S11.6, khuôn
 * `nccProfileApi` + `organizationAdminApi`). Toàn bộ type lấy từ
 * {@code generated-schema} (regenerate từ contract, KHÔNG sửa tay); UI không tự
 * suy diễn/mask. Backend vẫn là nơi enforce RBAC + data scope.
 */
export type MartyrListRow = components["schemas"]["MartyrListRow"];
export type MartyrPageData = components["schemas"]["MartyrPageData"];
export type MartyrPageMeta = components["schemas"]["PageMeta"];

/** Trang kết quả search: `rows` từ `data` + `meta` phân trang từ envelope. */
export type MartyrSearchPage = Readonly<{
  rows: MartyrPageData["rows"];
  meta: MartyrPageMeta | null;
}>;
export type MartyrData = components["schemas"]["MartyrData"];
export type MartyrCommandData = components["schemas"]["MartyrCommandData"];
export type CreateMartyrInput = components["schemas"]["CreateMartyrRequest"];
export type UpdateMartyrInput = components["schemas"]["UpdateMartyrRequest"];

/** Union typed theo tab (CR-S11-6-001 delta 3) — KHÔNG còn {@code items: object} mở. */
export type MartyrTabData = components["schemas"]["MartyrTabData"];

/** Khóa tab whitelist — suy trực tiếp từ discriminator của union contract. */
export type MartyrTabKey = MartyrTabData["tab"];

/**
 * Ánh xạ tab → đúng variant của union `MartyrTabData`. Dùng phân phối trên union
 * (không dùng `Extract`) vì `tab` của variant rỗng là union ba literal
 * (`search-missions | dna | public-history`), nên `Extract` sẽ cho `never`.
 */
export type MartyrTabVariant<K extends MartyrTabKey> = MartyrTabData extends infer Variant
  ? Variant extends { readonly tab: infer TabKey }
    ? K extends TabKey
      ? Variant
      : never
    : never
  : never;

/** Whitelist sáu tab SCR-026 đúng thứ tự hiển thị (đồng bộ enum getMartyrTab). */
export const MARTYR_TAB_ORDER: readonly MartyrTabKey[] = [
  "documents",
  "relatives",
  "search-missions",
  "graves",
  "dna",
  "public-history",
];

/** Bộ lọc searchMartyrs — đúng whitelist transport của contract. */
export type MartyrSearchFilters = Readonly<{
  page?: number;
  size?: number;
  q?: string;
  identityStatus?: string;
  unitName?: string;
  sacrificeFromDate?: string;
  sacrificeToDate?: string;
  fatherlandMeritNo?: string;
  nativePlace?: string;
  birthYearFrom?: number;
  birthYearTo?: number;
  cemeteryId?: string;
  graveCode?: string;
  sort?: string;
}>;

/** Bộ lọc export — cùng predicate, không phân trang (rowLimit do server). */
export type MartyrExportFilters = Omit<MartyrSearchFilters, "page" | "size">;

const BASE_PATH = "/martyrs";

function signalInit(signal?: AbortSignal): RequestInit {
  return signal ? { signal } : {};
}

function commandHeaders(idempotencyKey: string): Record<string, string> {
  return { "Idempotency-Key": idempotencyKey, "X-Request-Id": createRequestId() };
}

function encodeQuery(entries: Array<[string, string]>): string {
  const query = new URLSearchParams();
  for (const [key, value] of entries) query.set(key, value);
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

function filterEntries(filters: MartyrSearchFilters): Array<[string, string]> {
  const entries: Array<[string, string]> = [];
  const pushText = (key: string, value: string | undefined): void => {
    if (value !== undefined && value !== "") entries.push([key, value]);
  };
  const pushNumber = (key: string, value: number | undefined): void => {
    if (value !== undefined) entries.push([key, String(value)]);
  };
  pushNumber("page", filters.page);
  pushNumber("size", filters.size);
  pushText("q", filters.q);
  pushText("identityStatus", filters.identityStatus);
  pushText("unitName", filters.unitName);
  pushText("sacrificeFromDate", filters.sacrificeFromDate);
  pushText("sacrificeToDate", filters.sacrificeToDate);
  pushText("fatherlandMeritNo", filters.fatherlandMeritNo);
  pushText("nativePlace", filters.nativePlace);
  pushNumber("birthYearFrom", filters.birthYearFrom);
  pushNumber("birthYearTo", filters.birthYearTo);
  pushText("cemeteryId", filters.cemeteryId);
  pushText("graveCode", filters.graveCode);
  pushText("sort", filters.sort);
  return entries;
}

function postCommand<T>(
  path: string,
  body: unknown,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
    ...signalInit(signal),
  });
}

function patchCommand<T>(
  path: string,
  body: unknown,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, {
    method: "PATCH",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
    ...signalInit(signal),
  });
}

async function toApiProblem(response: Response): Promise<ApiProblem | ApiContractError> {
  const headerRequestId = response.headers.get("X-Request-Id") ?? undefined;
  try {
    const body = (await response.json()) as ProblemDetails;
    if (body && typeof body.code === "string") {
      return new ApiProblem(response.status, body);
    }
  } catch {
    // fall through — body không phải problem+json
  }
  return new ApiContractError(
    `unexpected export response (HTTP ${response.status})`,
    response.status,
    headerRequestId,
  );
}

/**
 * {@code GET /martyrs} — trang danh sách đã mask theo phạm vi dữ liệu người gọi
 * (operationId {@code searchMartyrs}). Bộ lọc rỗng/không truyền bị bỏ hẳn khỏi
 * query string, không gửi chuỗi rỗng.
 *
 * <p>`data` của operation chỉ mang `rows`; `PageMeta` nằm ở tầng envelope nên phải đọc
 * qua {@link apiRequestWithMeta} — nếu không, `total` không tới được `Pagination`.</p>
 */
export async function searchMartyrs(
  filters: MartyrSearchFilters,
  signal?: AbortSignal,
): Promise<MartyrSearchPage> {
  const page = await apiRequestWithMeta<MartyrPageData, MartyrPageMeta>(
    `${BASE_PATH}${encodeQuery(filterEntries(filters))}`,
    signalInit(signal),
  );
  return { rows: page.data.rows, meta: page.meta };
}

/**
 * {@code GET /martyrs/export} — quyền riêng EXPORT_EXECUTE + audit server-side.
 * `apiRequest` chỉ parse envelope JSON nên đường CSV dùng fetch trực tiếp với cùng
 * header xác thực; lỗi chuẩn hóa thành {@code ApiProblem} để UI hiển thị "Mã hỗ trợ"
 * và phân nhánh 403 thống nhất.
 */
export async function exportMartyrs(
  filters: MartyrExportFilters,
  signal?: AbortSignal,
): Promise<Blob> {
  const path = `${BASE_PATH}/export${encodeQuery(filterEntries(filters))}`;
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  const authentication = getAuthenticationSession();
  const headers = new Headers({
    Accept: "text/csv, application/problem+json",
    Authorization: `Bearer ${await authentication.getAccessToken()}`,
    "X-Request-Id": createRequestId(),
  });
  const response = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers,
    ...signalInit(signal),
  });
  if (!response.ok) {
    throw await toApiProblem(response);
  }
  return response.blob();
}

/**
 * {@code GET /martyrs/{martyrId}} — projection 360 đã mask (operationId {@code getMartyr}).
 * {@code martyrId} là định danh kỹ thuật dùng để định tuyến, KHÔNG hiển thị lên UI.
 */
export function getMartyr(martyrId: string, signal?: AbortSignal): Promise<MartyrData> {
  return apiRequest<MartyrData>(
    `${BASE_PATH}/${encodeURIComponent(martyrId)}`,
    signalInit(signal),
  );
}

/**
 * {@code GET /martyrs/{martyrId}/{tab}} — lazy-load MỘT tab SCR-026 đã mask
 * (operationId {@code getMartyrTab}). Kiểu trả về được thu hẹp theo `tab` nhờ
 * {@link MartyrTabVariant}; tab chưa có nguồn trả `rows: []` typed trung thực.
 */
export function getMartyrTab<K extends MartyrTabKey>(
  martyrId: string,
  tab: K,
  signal?: AbortSignal,
): Promise<MartyrTabVariant<K>> {
  return apiRequest<MartyrTabVariant<K>>(
    `${BASE_PATH}/${encodeURIComponent(martyrId)}/${encodeURIComponent(tab)}`,
    signalInit(signal),
  );
}

/** {@code POST /martyrs} — tạo hồ sơ liệt sĩ; lệnh ghi bắt buộc Idempotency-Key. */
export function createMartyr(
  input: CreateMartyrInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<MartyrCommandData> {
  return postCommand<MartyrCommandData>(BASE_PATH, input, idempotencyKey, signal);
}

/**
 * {@code PATCH /martyrs/{martyrId}} — cập nhật hồ sơ; contract không cho đổi
 * personId/nccProfileId/martyrCode qua patch.
 */
export function updateMartyr(
  martyrId: string,
  input: UpdateMartyrInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<MartyrCommandData> {
  return patchCommand<MartyrCommandData>(
    `${BASE_PATH}/${encodeURIComponent(martyrId)}`,
    input,
    idempotencyKey,
    signal,
  );
}
